import { PersonAdd as PersonAddIcon } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { useCallback, useState } from "react";

import { Dialog } from "../../atoms/Dialog/Dialog";
import { Form, type FormProps, ResetButton, SubmitButton, type TextFieldValue } from "../../atoms/Form";
import {
  Email,
  FIELD_NAME_EMAIL,
  FIELD_NAME_FIRST_NAME,
  FIELD_NAME_LAST_NAME,
  FirstName,
  LastName,
} from "../../molecules/Form";

// Centralized strings for internationalization
const tAddMember = "Add Member";
const tCancel = "Cancel";
const tAdd = "Add Member";

const requiredFields = [FIELD_NAME_FIRST_NAME, FIELD_NAME_LAST_NAME, FIELD_NAME_EMAIL];

interface FormValues {
  [FIELD_NAME_FIRST_NAME]: TextFieldValue;
  [FIELD_NAME_LAST_NAME]: TextFieldValue;
  [FIELD_NAME_EMAIL]: TextFieldValue;
}

interface NewMember {
  firstName: string;
  lastName: string;
  email: string;
}

interface AddMemberDialogProps {
  onAddMember: (member: NewMember) => void;
}

export const AddMemberDialog = ({ onAddMember }: AddMemberDialogProps) => {
  const [open, setOpen] = useState(false);

  const closeDialog = useCallback(() => setOpen(false), []);
  const openDialog = useCallback(() => setOpen(true), []);

  const handleSubmit = useCallback<FormProps<FormValues>["onSubmit"]>(
    (formValues: FormValues) => {
      const firstName = formValues[FIELD_NAME_FIRST_NAME];
      const lastName = formValues[FIELD_NAME_LAST_NAME];
      const email = formValues[FIELD_NAME_EMAIL];

      if (firstName && lastName && email) {
        const member: NewMember = {
          email,
          firstName,
          lastName,
        };

        onAddMember(member);
        closeDialog();
      }
    },
    [onAddMember, closeDialog],
  );

  return (
    <>
      <Button
        variant="contained"
        startIcon={<PersonAddIcon />}
        onClick={openDialog}
        fullWidth
        sx={{
          borderRadius: "12px",
          fontWeight: "bold",
          textTransform: "none",
        }}
      >
        {tAddMember}
      </Button>

      <Form<FormValues> onSubmit={handleSubmit}>
        <Dialog
          actions={
            <Box display="grid" gap="2rem" gridAutoColumns="minmax(0, 1fr)" gridAutoFlow="row" padding="1rem">
              <FirstName required />
              <LastName required />
              <Email required />
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <ResetButton onClick={closeDialog}>{tCancel}</ResetButton>
                <SubmitButton onClick={closeDialog} requiredFields={requiredFields}>
                  {tAdd}
                </SubmitButton>
              </Box>
            </Box>
          }
          dialogType="custom"
          maxWidth="sm"
          onDialogClose={closeDialog}
          open={open}
          title={tAddMember}
        />
      </Form>
    </>
  );
};
